import { isSlackTsLater } from "./insert-admin-message";
import { toSafeAdminMessage } from "./safe-admin-message";
import {
  AdminMessage,
  Message,
  ReadCursor,
  SupportChatHistoryMessage,
  SupportThreadSnapshot,
  ThreadState,
  UserMessage,
} from "./types";

const adminReplies = (messages: Message[]): AdminMessage[] =>
  messages.filter((message): message is AdminMessage => message.kind === "admin");

// Display order isn't ts order: a delayed reply never moves above the planner's later message.
const adminTsAscending = (messages: Message[]): string[] =>
  adminReplies(messages)
    .map((reply) => reply.ts)
    .sort((a, b) => (isSlackTsLater(a, b) ? 1 : isSlackTsLater(b, a) ? -1 : 0));

/**
 * The `ts` of the newest admin reply in a thread: where the read cursor lands when the planner reads it all.
 *
 * @param messages - The thread, oldest first.
 * @returns That `ts`, or null when support hasn't replied.
 */
export const latestAdminTs = (messages: Message[]): ReadCursor => adminTsAscending(messages).at(-1) ?? null;

/**
 * The later of two read cursors, so a read the planner made can't be undone by an older answer.
 *
 * @param a - One cursor.
 * @param b - The other cursor.
 * @returns Whichever reads further; null only when both are null.
 */
export const laterCursor = (a: ReadCursor, b: ReadCursor): ReadCursor => {
  if (a === null) return b;
  if (b === null) return a;
  return isSlackTsLater(b, a) ? b : a;
};

/**
 * How many admin replies the planner hasn't read.
 *
 * @param messages - The thread, oldest first.
 * @param readThrough - The read cursor.
 * @returns The count of admin replies later than the cursor (all of them when it is null).
 */
export const countUnread = (messages: Message[], readThrough: ReadCursor): number =>
  adminReplies(messages).filter((reply) => readThrough === null || isSlackTsLater(reply.ts, readThrough)).length;

const fromHistory = (message: SupportChatHistoryMessage): Message | null => {
  if (message.kind === "user") return { id: message.id, kind: "user", text: message.text, status: "sent" };
  const { createdAt: _createdAt, ...reply } = message;
  return toSafeAdminMessage(reply);
};

const cursorLeaving = (unread: number, messages: Message[]): ReadCursor => {
  const ts = adminTsAscending(messages);
  const count = Math.min(unread, ts.length);
  return count === ts.length ? null : ts[ts.length - 1 - count];
};

/**
 * The planner's local messages the snapshot can't account for: failed ones,
 * and ones still sending or sent while the request was out, minus those the
 * server already stored. The POST doesn't return the stored id, so a server
 * user message the tab hasn't seen yet stands for the oldest local message
 * with the same text (earlier sends claim first, so a repeat stays).
 */
const notYetStored = (local: Message[], server: Message[], sentDuringFetch: ReadonlySet<string>): UserMessage[] => {
  const localIds = new Set(local.map((message) => message.id));
  const serverIds = new Set(server.map((message) => message.id));
  const unseen = server.filter((message) => message.kind === "user" && !localIds.has(message.id));
  const kept: UserMessage[] = [];
  for (const message of local) {
    if (message.kind !== "user" || message.legacy || serverIds.has(message.id)) continue;
    if (message.status === "failed") {
      kept.push(message);
      continue;
    }
    const copy = unseen.findIndex((stored) => stored.text === message.text);
    if (copy !== -1) unseen.splice(copy, 1);
    else if (message.status === "sending" || sentDuringFetch.has(message.id)) kept.push(message);
  }
  return kept;
};

/**
 * Folds the server's copy of the thread into what the tab shows. The server is
 * the source of truth: when it has a thread with messages, they replace the
 * local ones, keeping only what it can't know yet: legacy messages it doesn't
 * have (saved before it stored history, kept above its own), admin replies
 * the socket delivered after the server answered (later than its newest
 * message, or delivered while the request was out, however old their ts), and the planner's messages the snapshot may predate (unsent, failed, or sent while
 * the request was out) unless the server already has them. A thread with no
 * stored messages predates server history, so the local messages stay. The
 * cursor sits so exactly `unread` of the thread's admin replies stay unread.
 * When the server has no thread, the local thread stays on screen (a closed
 * thread, or nothing) and counts as read.
 *
 * @param local - What the tab shows now.
 * @param snapshot - `GET /support-chat/thread`'s answer.
 * @param changedDuringFetch - Ids of local messages that finished sending, or replies the socket delivered,
 *   after the request for `snapshot` started.
 * @returns The thread to show.
 */
export const mergeServerThread = (
  local: ThreadState,
  snapshot: SupportThreadSnapshot,
  changedDuringFetch: ReadonlySet<string> = new Set()
): ThreadState => {
  if (snapshot.thread === null) return { ...local, readThrough: latestAdminTs(local.messages) };

  const threadStatus = snapshot.thread.status === "open" ? "active" : "closed";
  const history = snapshot.thread.messages;
  if (history.length === 0) {
    return { messages: local.messages, threadStatus, readThrough: cursorLeaving(snapshot.unread, local.messages) };
  }

  const server = history.map(fromHistory).filter((message): message is Message => message !== null);
  const newestServerTs = history[history.length - 1].ts;
  const serverIds = new Set(history.map((message) => message.id));
  const legacy = local.messages.filter((message) => message.legacy && !serverIds.has(message.id));
  const arrivedSince = adminReplies(local.messages).filter(
    (reply) =>
      !reply.legacy &&
      !serverIds.has(reply.id) &&
      (isSlackTsLater(reply.ts, newestServerTs) || changedDuringFetch.has(reply.id))
  );
  const thread = [...legacy, ...server];

  return {
    messages: [...thread, ...arrivedSince, ...notYetStored(local.messages, server, changedDuringFetch)],
    threadStatus,
    readThrough: cursorLeaving(snapshot.unread, thread),
  };
};
